import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RotateCcw, ExternalLink, GitMerge, Archive, X as XIcon, History, Download, Eye } from 'lucide-react';
import { T, knopfStil, primaerKnopfStil } from '../../lib/designTokens';
import MehrMenue from './MehrMenue';
// Laden, Vorbereiten, Auslesen und Speichern teilt sich das Studio seit dem
// 23.09.2026 mit der mobilen Oberfläche (reines Verschieben nach src/lib).
import {
  applyEditorStateToTemplate,
  cleanContractHtmlForStorage,
  ladeStudioStand,
  leseIframeStand,
  materializeContractStateHtml,
  prepareContractHtmlForStudio,
  protectedIdsFromEditorState,
  protectedIdsFromSourceHtml,
  speichereVorlagenStand,
} from '../../lib/vorlagenEditor';

export const CONTRACTS = [
  {
    key: 'vermittlervertrag',
    label: 'Vermittlervertrag',
    variants: [
      { key: 'ci', label: 'CI', file: 'Vermittlervertrag_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Vermittlervertrag_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'kaufvertrag-kleinspeicher',
    label: 'Kaufvertrag Kleinspeicher / Schwarmspeicher',
    variants: [
      { key: 'ci', label: 'CI', file: 'Kaufvertrag_Kleinspeicher_Schwarmspeicher_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Kaufvertrag_Kleinspeicher_Schwarmspeicher_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'projektrechte-pv',
    label: 'Projektrechte PV Kaufvertrag',
    variants: [
      { key: 'ci', label: 'CI', file: 'Projektrechte_PV_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Projektrechte_PV_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'dachnutzungsvertrag-sanierung',
    label: 'Dachnutzungsvertrag Sanierung',
    variants: [
      { key: 'ci', label: 'CI', file: 'Dachnutzungsvertrag_Sanierung_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Dachnutzungsvertrag_Sanierung_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-dachverpachtung-ez',
    label: 'Pachtvertrag Dachverpachtung (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Dachverpachtung_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Dachverpachtung_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-dachverpachtung-jz',
    label: 'Pachtvertrag Dachverpachtung (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Dachverpachtung_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Dachverpachtung_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-kleinspeicher-ez',
    label: 'Pachtvertrag Kleinspeicher (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Kleinspeicher_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Kleinspeicher_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-kleinspeicher-jz',
    label: 'Pachtvertrag Kleinspeicher (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-grossspeicher-ez',
    label: 'Pachtvertrag Großspeicher (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Grossspeicher_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Grossspeicher_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-grossspeicher-jz',
    label: 'Pachtvertrag Großspeicher (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Grossspeicher_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Grossspeicher_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'aufhebungsvereinbarung',
    label: 'Aufhebungsvereinbarung',
    variants: [
      { key: 'ci', label: 'CI', file: 'Aufhebungsvereinbarung_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Aufhebungsvereinbarung_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'kuendigung-eigentuemervertrag',
    label: 'Kündigung Eigentümervertrag',
    variants: [
      { key: 'ci', label: 'CI', file: 'Kuendigung_Eigentuemervertrag_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Kuendigung_Eigentuemervertrag_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    // Neutrale Vorlage (30.10.2026): EN:POWER ist nicht Partei, daher ohne
    // Logo - die Variante heisst "Standard", der Schluessel bleibt 'ci'.
    key: 'werkliefervertrag-pv',
    label: 'Werkliefervertrag PV',
    variants: [
      { key: 'ci', label: 'Standard', file: 'Werkliefervertrag_PV.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Werkliefervertrag_PV_Druckversion.html' },
    ],
  },
];

function buildContractStateKey(contractKey) {
  return contractKey;
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function formatVersionDate(value) {
  if (!value) return 'Unbekannter Zeitpunkt';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unbekannter Zeitpunkt' : date.toLocaleString('de-DE');
}

function formatVersionSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

async function readAxiosErrorDetail(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.detail || fallback;
    } catch (_parseError) {
      return fallback;
    }
  }
  return data?.detail || error?.message || fallback;
}

function buildLegacyContractStateKey(contractKey, variantKey) {
  return `${contractKey}__${variantKey}`;
}

function findSiblingVariantKey(contract, variantKey) {
  if (!contract || !Array.isArray(contract.variants) || contract.variants.length < 2) {
    return null;
  }
  const other = contract.variants.find((variant) => variant.key !== variantKey);
  return other ? other.key : null;
}

function injectBaseHref(html, baseHref) {
  if (typeof html !== 'string' || !html) {
    return html;
  }
  if (/<base\s/i.test(html)) {
    return html;
  }
  const baseTag = `<base href="${baseHref}">`;
  if (/<head([^>]*)>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
  }
  return html;
}

function findPaymentSiblingContract(contractKey, documents = CONTRACTS) {
  const key = String(contractKey || '');
  const siblingKey = key.endsWith('-jz')
    ? key.replace(/-jz$/, '-ez')
    : (key.endsWith('-ez') ? key.replace(/-ez$/, '-jz') : '');
  if (!siblingKey) return null;
  return documents.find((contract) => contract.key === siblingKey) || null;
}

function extractSourceHtmlFromTemplate(html) {
  if (typeof html !== 'string' || !html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById('contract-source')?.innerHTML || '';
  } catch (_error) {
    return '';
  }
}

function cssEscapeSelector(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(String(value || ''));
  }
  return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function mergeSourceHtmlPreservingProtected(sourceHtml, targetSourceHtml) {
  if (typeof sourceHtml !== 'string' || !sourceHtml) return targetSourceHtml || '';
  if (typeof targetSourceHtml !== 'string' || !targetSourceHtml) return sourceHtml;
  const sourceWrapper = document.createElement('div');
  const targetWrapper = document.createElement('div');
  sourceWrapper.innerHTML = sourceHtml;
  targetWrapper.innerHTML = targetSourceHtml;

  const sourceProtected = protectedIdsFromSourceHtml(sourceHtml);
  const targetProtected = protectedIdsFromSourceHtml(targetSourceHtml);
  const protectedIds = new Set([...sourceProtected, ...targetProtected]);

  protectedIds.forEach((id) => {
    if (!id) return;
    const escapedId = cssEscapeSelector(id);
    const targetNode = targetWrapper.querySelector(`[data-editor-id="${escapedId}"]`);
    const sourceNode = sourceWrapper.querySelector(`[data-editor-id="${escapedId}"]`);
    if (targetNode && sourceNode) {
      sourceNode.replaceWith(targetNode.cloneNode(true));
    }
  });

  return sourceWrapper.innerHTML;
}

function mergeEditorStatesPreservingProtected(sourceEditorState, targetEditorState, mergedSourceHtml) {
  const next = sourceEditorState && typeof sourceEditorState === 'object' ? { ...sourceEditorState } : {};
  const sourceBlocks = sourceEditorState?.blocks && typeof sourceEditorState.blocks === 'object' ? sourceEditorState.blocks : {};
  const targetBlocks = targetEditorState?.blocks && typeof targetEditorState.blocks === 'object' ? targetEditorState.blocks : {};
  const protectedIds = new Set([
    ...protectedIdsFromEditorState(sourceEditorState),
    ...protectedIdsFromEditorState(targetEditorState),
    ...protectedIdsFromSourceHtml(sourceEditorState?.sourceHtml || ''),
    ...protectedIdsFromSourceHtml(targetEditorState?.sourceHtml || ''),
  ]);

  next.blocks = { ...sourceBlocks };
  protectedIds.forEach((id) => {
    if (targetBlocks[id]) next.blocks[id] = targetBlocks[id];
  });
  next.sourceHtml = mergedSourceHtml;
  next.syncedFromPaymentVariant = sourceEditorState?.contractKey || '';
  return next;
}

function inferClauseInsertPositionFromNode(node, doc) {
  if (!node || !doc) return 'before-signatures';

  const source = doc.getElementById('contract-source');
  if (!source) return 'before-signatures';

  const children = Array.from(source.children || []);
  const nodeIndex = children.indexOf(node);
  if (nodeIndex < 0) return 'before-signatures';

  const firstSectionIndex = children.findIndex((candidate) => candidate.matches?.('h2.section-heading, h3.clause-heading, h3.annex-subheading'));
  const signatureIndex = children.findIndex((candidate) => /unterschrift|signatur|signature/i.test(candidate.textContent || ''));
  const annexIndex = children.findIndex((candidate) => /^anlage\b/i.test((candidate.textContent || '').trim()));

  if (annexIndex >= 0 && nodeIndex >= annexIndex) return 'end-of-contract';
  if (signatureIndex >= 0 && nodeIndex >= signatureIndex) return 'before-signatures';
  if (firstSectionIndex >= 0 && nodeIndex <= firstSectionIndex) return 'after-preamble';
  return 'before-signatures';
}

function detectStructureType(node) {
  if (!node) return { key: 'paragraph', label: 'Absatz' };
  if (node.matches('h2.section-heading')) return { key: 'section-heading', label: 'Überschrift' };
  if (node.matches('h3.clause-heading')) return { key: 'clause-heading', label: 'Unterüberschrift' };
  if (node.matches('h3.annex-subheading')) return { key: 'annex-subheading', label: 'Anlagen-Unterüberschrift' };
  if (node.matches('table.data-table')) return { key: 'table', label: 'Tabelle' };
  if (node.classList.contains('clause-paragraph')) return { key: 'clause', label: 'Nummerierter Absatz' };
  if (node.classList.contains('bullet-line')) return { key: 'bullet', label: 'Aufzählungspunkt' };
  if (node.classList.contains('checkbox-line')) return { key: 'checkbox', label: 'Checkbox-Zeile' };
  if (node.classList.contains('clause-detail')) return { key: 'detail', label: 'Detailzeile' };
  if (node.classList.contains('value-line')) return { key: 'value', label: 'Wertfeld' };
  if (node.classList.contains('lead-line')) return { key: 'lead', label: 'Einleitung' };
  return { key: 'paragraph', label: 'Absatz' };
}

function getSectionContext(node, doc) {
  if (!node || !doc) return { label: '', anchorId: '' };
  const source = doc.getElementById('contract-source');
  if (!source) return { label: '', anchorId: '' };

  const children = Array.from(source.children || []);
  const nodeIndex = children.indexOf(node);
  if (nodeIndex < 0) return { label: '', anchorId: '' };

  for (let index = nodeIndex; index >= 0; index -= 1) {
    const candidate = children[index];
    if (!candidate) continue;
    if (candidate.matches?.('h2.section-heading, h3.clause-heading, h3.annex-subheading')) {
      return {
        label: (candidate.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120),
        anchorId: candidate.dataset?.editorId || '',
      };
    }
  }

  return { label: '', anchorId: '' };
}

// Beschriftung ueber einem Feld - normale Schrift statt gesperrter Versalien.
const FELD_BESCHRIFTUNG = { fontSize: T.tMikro, fontWeight: 700, color: T.muted };

export default function VertragsStudio({
  apiBase = '/api',
  session = null,
  documents = CONTRACTS,
  basePath = '/contracts/',
  typeLabel = 'Vertragsart',
  recordLabel = 'Vertrag',
  originalButtonLabel = 'Original',
  mergeOriginalMissingSections = true,
  enableSiblingSync = true,
  enablePaymentSync = true,
  enableExampleClauses = true,
  editorScope = '',
}) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);
  const documentOptions = useMemo(
    () => (Array.isArray(documents) && documents.length > 0 ? documents : CONTRACTS),
    [documents]
  );
  const initialDocument = documentOptions[0] || CONTRACTS[0];
  const initialVariant = initialDocument.variants?.[0] || CONTRACTS[0].variants[0];
  const normalizedBasePath = useMemo(() => {
    const value = String(basePath || '/contracts/');
    return value.endsWith('/') ? value : `${value}/`;
  }, [basePath]);

  const [activeContractKey, setActiveContractKey] = useState(initialDocument.key);
  const [activeVariantKey, setActiveVariantKey] = useState(initialVariant.key);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [iframeSrcDoc, setIframeSrcDoc] = useState(null);
  const [loadingState, setLoadingState] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [paymentSyncing, setPaymentSyncing] = useState(false);
  const [hasSavedState, setHasSavedState] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [syncedFromVariant, setSyncedFromVariant] = useState(null);
  const [exampleClauseDraft, setExampleClauseDraft] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionBusyFile, setVersionBusyFile] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [exporting, setExporting] = useState(false);
  const iframeRef = useRef(null);

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  useEffect(() => {
    const selectedDocument = documentOptions.find((contract) => contract.key === activeContractKey);
    if (!selectedDocument) {
      const fallbackDocument = documentOptions[0] || CONTRACTS[0];
      setActiveContractKey(fallbackDocument.key);
      setActiveVariantKey(fallbackDocument.variants?.[0]?.key || CONTRACTS[0].variants[0].key);
      return;
    }
    if (!selectedDocument.variants.some((variant) => variant.key === activeVariantKey)) {
      setActiveVariantKey(selectedDocument.variants[0]?.key || '');
    }
  }, [activeContractKey, activeVariantKey, documentOptions]);

  const activeContract = useMemo(() => (
    documentOptions.find((contract) => contract.key === activeContractKey) || documentOptions[0] || CONTRACTS[0]
  ), [activeContractKey, documentOptions]);

  const activeVariant = useMemo(() => {
    const variants = activeContract.variants;
    return variants.find((variant) => variant.key === activeVariantKey) || variants[0];
  }, [activeContract, activeVariantKey]);

  const stateKey = useMemo(
    () => buildContractStateKey(activeContract.key),
    [activeContract.key]
  );

  const legacyStateKey = useMemo(
    () => buildLegacyContractStateKey(activeContract.key, activeVariant.key),
    [activeContract.key, activeVariant.key]
  );

  const siblingVariant = useMemo(() => {
    if (!enableSiblingSync) return null;
    const siblingKey = findSiblingVariantKey(activeContract, activeVariant.key);
    if (!siblingKey) return null;
    return activeContract.variants.find((variant) => variant.key === siblingKey) || null;
  }, [activeContract, activeVariant.key, enableSiblingSync]);

  const paymentSiblingContract = useMemo(
    () => (enablePaymentSync ? findPaymentSiblingContract(activeContract.key, documentOptions) : null),
    [activeContract.key, documentOptions, enablePaymentSync]
  );

  const originalUrl = `${normalizedBasePath}${activeVariant.file}`;

  const loadState = useCallback(async () => {
    if (!isAuthenticated) {
      setIframeSrc(originalUrl);
      setIframeSrcDoc(null);
      setHasSavedState(false);
      setSavedAt(null);
      setSyncedFromVariant(null);
      setIframeKey((value) => value + 1);
      return;
    }

    setLoadingState(true);
    try {
      const geladen = await ladeStudioStand({
        apiBase,
        authHeaders,
        stateKey,
        legacyStateKey,
        originalUrl,
        contractKey: activeContract.key,
        variantKey: activeVariant.key,
        editorScope,
        mergeOriginalMissingSections,
      });
      setIframeSrc(null);
      setIframeSrcDoc(injectBaseHref(geladen.html, normalizedBasePath));
      setHasSavedState(geladen.hatStand);
      setSavedAt(geladen.savedAt);
      setSyncedFromVariant(geladen.syncedFrom);
      setIframeKey((value) => value + 1);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      if (detail) {
        toast.error(`Vertrags-Stand konnte nicht geladen werden: ${detail}`);
      }
      setIframeSrc(originalUrl);
      setIframeSrcDoc(null);
      setHasSavedState(false);
      setSavedAt(null);
      setSyncedFromVariant(null);
      setIframeKey((value) => value + 1);
    } finally {
      setLoadingState(false);
    }
  }, [activeContract.key, activeVariant.key, apiBase, authHeaders, editorScope, isAuthenticated, legacyStateKey, mergeOriginalMissingSections, normalizedBasePath, originalUrl, stateKey]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const readIframeHtml = useCallback(() => leseIframeStand(iframeRef.current), []);

  const handleSave = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    setSaving(true);
    try {
      const { content, editorState, protectionCount } = readIframeHtml();
      const antwort = await speichereVorlagenStand({ apiBase, authHeaders, stateKey, content, editorState, protectionCount });
      const gilt = siblingVariant ? ` Gilt auch für „${siblingVariant.label}".` : '';
      toast.success(antwort?.backup
        ? `Vertrags-Stand gespeichert. Sicherheits-Backup wurde erstellt.${gilt}`
        : `Vertrags-Stand gespeichert.${gilt}`);
      setHasSavedState(true);
      setSavedAt(new Date().toISOString());
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, readIframeHtml, siblingVariant, stateKey]);

  const sanitizeExampleClauseHtml = useCallback((node) => {
    if (!node) return '';
    const clone = node.cloneNode(true);
    clone.removeAttribute('data-editor-id');
    clone.removeAttribute('contenteditable');
    clone.removeAttribute('draggable');
    clone.querySelectorAll('[data-editor-handle]').forEach((handle) => handle.remove());
    clone.querySelectorAll('.editor-selected,.editor-dragging,.editor-drop-before,.editor-drop-after').forEach((element) => {
      element.classList.remove('editor-selected', 'editor-dragging', 'editor-drop-before', 'editor-drop-after');
    });
    return clone.outerHTML;
  }, []);

  const openExampleClauseDraft = useCallback(() => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) {
      toast.error('Vorschau nicht verfügbar.');
      return;
    }

    let doc;
    try {
      doc = iframe.contentDocument;
    } catch (error) {
      toast.error('Konnte den Editor-Inhalt nicht lesen.');
      return;
    }

    const selectedNode = doc.querySelector('#contract-pages .editor-selected, #contract-source .editor-selected, .editor-selected');
    if (!selectedNode) {
      toast.error('Bitte zuerst einen Absatz im Editor auswählen.');
      return;
    }

    const defaultTitle = (selectedNode.textContent || '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 70) || 'Beispiel-Paragraph';
    const structure = detectStructureType(selectedNode);
    const sectionContext = getSectionContext(selectedNode, doc);
    const anchorSide = structure.key === 'section-heading' ? 'after' : 'before';
    setExampleClauseDraft({
      title: defaultTitle,
      contentHtml: sanitizeExampleClauseHtml(selectedNode),
      anchorBlockId: selectedNode.dataset?.editorId || '',
      anchorSide,
      anchorLabel: (selectedNode.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120),
      structureKey: structure.key,
      structureLabel: structure.label,
      sectionLabel: sectionContext.label,
      sectionAnchorId: sectionContext.anchorId,
      insertPosition: inferClauseInsertPositionFromNode(selectedNode, doc),
      contractKey: activeContract.key,
      variantKey: activeVariant.key,
      blockTag: selectedNode.tagName ? selectedNode.tagName.toLowerCase() : '',
      className: selectedNode.className || '',
      style: selectedNode.getAttribute('style') || '',
    });
  }, [activeContract.key, activeVariant.key, isAuthenticated, sanitizeExampleClauseHtml]);

  const handlePersistExampleClause = useCallback(async () => {
    if (!exampleClauseDraft) return;
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: String(exampleClauseDraft.title || '').trim(),
        content: exampleClauseDraft.contentHtml,
        insert_position: exampleClauseDraft.insertPosition,
        contract_types: [exampleClauseDraft.contractKey],
        is_active: true,
        source_contract_key: exampleClauseDraft.contractKey,
        source_variant_key: exampleClauseDraft.variantKey,
        source_anchor_block_id: exampleClauseDraft.anchorBlockId,
        source_anchor_side: exampleClauseDraft.anchorSide,
        source_anchor_label: exampleClauseDraft.anchorLabel,
        source_structure_key: exampleClauseDraft.structureKey,
        source_structure_label: exampleClauseDraft.structureLabel,
        source_section_label: exampleClauseDraft.sectionLabel,
        source_section_anchor_id: exampleClauseDraft.sectionAnchorId,
        source_block_type: exampleClauseDraft.blockTag,
        source_class_name: exampleClauseDraft.className,
        source_style: exampleClauseDraft.style,
      };

      await axios.post(`${apiBase}/contracts/state?resource=clauses`, payload, { headers: authHeaders });
      setExampleClauseDraft(null);
      toast.success('Beispiel-Paragraph gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Beispiel-Paragraph konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }, [apiBase, authHeaders, exampleClauseDraft, isAuthenticated]);

  const handleCloseExampleClauseDraft = useCallback(() => {
    setExampleClauseDraft(null);
  }, []);

  const handleResetToOriginal = useCallback(async () => {
    if (!window.confirm(`Wirklich auf das Original-Template zurücksetzen? Deine Änderungen für diesen ${recordLabel} gehen verloren.`)) {
      return;
    }
    if (!isAuthenticated) {
      setIframeSrc(originalUrl);
      setIframeSrcDoc(null);
      setHasSavedState(false);
      setSavedAt(null);
      setIframeKey((value) => value + 1);
      return;
    }
    setSaving(true);
    try {
      // Speichere einen leeren Reset (content: leer string), damit der nächste Load wieder zum Original geht
      await axios.post(`${apiBase}/contracts/state`, {
        contract_key: stateKey,
        content: '',
      }, {
        headers: authHeaders,
      });
      toast.success('Auf Original-Template zurückgesetzt.');
      await loadState();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, loadState, originalUrl, recordLabel, stateKey]);

  const handleSyncToSibling = useCallback(async () => {
    if (!siblingVariant) return;
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!window.confirm(
      `Aktuellen Stand auf Variante „${siblingVariant.label}" übertragen? `
      + `Ein vorhandener gespeicherter Stand der „${siblingVariant.label}"-Variante wird dabei überschrieben.`
    )) {
      return;
    }
    setSyncing(true);
    try {
      const { content, editorState, protectionCount } = readIframeHtml();
      await axios.post(`${apiBase}/contracts/state`, {
        contract_key: stateKey,
        content,
        editor_state: editorState || undefined,
        create_backup: protectionCount > 0,
        backup_reason: protectionCount > 0 ? 'before-ci-print-sync-save' : undefined,
      }, {
        headers: authHeaders,
      });

      toast.success(`Stand gespeichert und auf „${siblingVariant.label}" übertragen.`);
      setHasSavedState(true);
      setSavedAt(new Date().toISOString());
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Synchronisieren fehlgeschlagen.');
    } finally {
      setSyncing(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, readIframeHtml, siblingVariant, stateKey]);

  const handleSyncToPaymentSibling = useCallback(async () => {
    if (!paymentSiblingContract) return;
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    const targetVariant = paymentSiblingContract.variants.find((variant) => variant.key === activeVariant.key)
      || paymentSiblingContract.variants[0];
    if (!targetVariant) return;
    if (!window.confirm(
      `Gemeinsame Bereiche auf "${paymentSiblingContract.label}" übertragen? `
      + 'Bereiche mit EZ/JZ-Schutz bleiben in der Zielvorlage erhalten. Vorher wird ein Backup erstellt.'
    )) {
      return;
    }

    setPaymentSyncing(true);
    try {
      const { content, editorState, protectionCount } = readIframeHtml();
      const sourceEditorState = editorState ? { ...editorState, contractKey: activeContract.key } : { contractKey: activeContract.key };

      await axios.post(`${apiBase}/contracts/state`, {
        contract_key: stateKey,
        content,
        editor_state: sourceEditorState,
        create_backup: protectionCount > 0,
        backup_reason: protectionCount > 0 ? 'before-payment-variant-sync-source-save' : undefined,
      }, {
        headers: authHeaders,
      });

      const targetStateKey = buildContractStateKey(paymentSiblingContract.key);
      const targetStateResponse = await axios.get(`${apiBase}/contracts/state`, {
        params: { contract_key: targetStateKey },
        headers: authHeaders,
      });
      const targetState = targetStateResponse.data?.state || null;
      const targetOriginalUrl = `${normalizedBasePath}${targetVariant.file}`;
      const targetOriginalResponse = await fetch(targetOriginalUrl);
      if (!targetOriginalResponse.ok) {
        throw new Error('Ziel-Basisvorlage konnte nicht geladen werden.');
      }
      const targetOriginalHtml = await targetOriginalResponse.text();
      const targetResolved = materializeContractStateHtml(targetOriginalHtml, targetState, { preferSourceHtml: true });
      const targetHtml = targetResolved.html || targetOriginalHtml;
      const targetSourceHtml = targetState?.editorState?.sourceHtml
        || extractSourceHtmlFromTemplate(targetHtml)
        || extractSourceHtmlFromTemplate(targetOriginalHtml);
      const sourceHtml = sourceEditorState.sourceHtml || extractSourceHtmlFromTemplate(content);
      const mergedSourceHtml = mergeSourceHtmlPreservingProtected(sourceHtml, targetSourceHtml);
      const targetEditorState = mergeEditorStatesPreservingProtected(
        sourceEditorState,
        targetState?.editorState || null,
        mergedSourceHtml
      );
      targetEditorState.contractKey = paymentSiblingContract.key;
      targetEditorState.syncedFromPaymentVariant = activeContract.key;

      const targetContent = cleanContractHtmlForStorage(
        applyEditorStateToTemplate(targetOriginalHtml, targetEditorState),
        targetEditorState
      );

      await axios.post(`${apiBase}/contracts/state`, {
        contract_key: targetStateKey,
        content: targetContent,
        editor_state: targetEditorState,
        create_backup: true,
        backup_reason: 'before-payment-variant-sync-target-save',
      }, {
        headers: authHeaders,
      });

      toast.success(`Gemeinsame Bereiche auf "${paymentSiblingContract.label}" übertragen. Geschützte Bereiche blieben erhalten.`);
      setHasSavedState(true);
      setSavedAt(new Date().toISOString());
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'EZ/JZ-Übertragung fehlgeschlagen.');
    } finally {
      setPaymentSyncing(false);
    }
  }, [
    activeContract.key,
    activeVariant.key,
    apiBase,
    authHeaders,
    isAuthenticated,
    normalizedBasePath,
    paymentSiblingContract,
    readIframeHtml,
    stateKey,
  ]);

  const handleOpenInNewTab = useCallback(() => {
    window.open(originalUrl, '_blank', 'noopener,noreferrer');
  }, [originalUrl]);

  // --- Versionen & Export ---------------------------------------------------
  useEffect(() => {
    setVersionsOpen(false);
    setVersions([]);
    setPreviewVersion(null);
    setVersionBusyFile(null);
  }, [stateKey]);

  const loadVersions = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    setVersionsLoading(true);
    try {
      const response = await axios.get(`${apiBase}/contracts/state`, {
        params: { resource: 'versions', contract_key: stateKey },
        headers: authHeaders,
      });
      setVersions(Array.isArray(response.data?.versions) ? response.data.versions : []);
    } catch (error) {
      console.error(error);
      setVersions([]);
      toast.error(error?.response?.data?.detail || error?.message || 'Versionen konnten nicht geladen werden.');
    } finally {
      setVersionsLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, stateKey]);

  const handleToggleVersions = useCallback(() => {
    if (versionsOpen) {
      setVersionsOpen(false);
      return;
    }
    setVersionsOpen(true);
    loadVersions();
  }, [loadVersions, versionsOpen]);

  const handlePreviewVersion = useCallback(async (version) => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    setVersionBusyFile(version.file);
    try {
      const response = await axios.get(`${apiBase}/contracts/state`, {
        params: { resource: 'version', contract_key: stateKey, file: version.file },
        headers: authHeaders,
      });
      const data = response.data?.version || null;
      if (!data) {
        throw new Error('Diese Version enthält keine Daten.');
      }

      let html = data.content || null;
      if (!html) {
        const baseResponse = await fetch(originalUrl);
        if (!baseResponse.ok) {
          throw new Error('Basis-Template konnte nicht geladen werden.');
        }
        html = applyEditorStateToTemplate(await baseResponse.text(), data.editorState || null);
      }

      const prepared = prepareContractHtmlForStudio(html, {
        editorState: data.editorState || null,
        contractKey: activeContract.key,
        variantKey: activeVariant.key,
        editorScope,
        editable: true,
      });
      setIframeSrc(null);
      setIframeSrcDoc(injectBaseHref(prepared, normalizedBasePath));
      setIframeKey((value) => value + 1);
      setPreviewVersion({ file: version.file, createdAt: version.createdAt, actor: version.actor });
      toast.success('Version wird als Vorschau angezeigt – noch nichts gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Vorschau konnte nicht geladen werden.');
    } finally {
      setVersionBusyFile(null);
    }
  }, [
    activeContract.key,
    activeVariant.key,
    apiBase,
    authHeaders,
    editorScope,
    isAuthenticated,
    normalizedBasePath,
    originalUrl,
    stateKey,
  ]);

  const handleDiscardPreview = useCallback(async () => {
    setPreviewVersion(null);
    await loadState();
  }, [loadState]);

  const handleRestoreVersion = useCallback(async (version) => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!window.confirm(
      `Version vom ${formatVersionDate(version.createdAt)} wirklich wiederherstellen? `
      + 'Der aktuell gespeicherte Stand wird vorher automatisch gesichert und bleibt als Version erhalten.'
    )) {
      return;
    }

    setVersionBusyFile(version.file);
    try {
      await axios.post(`${apiBase}/contracts/state?resource=version-restore`, {
        contract_key: stateKey,
        file: version.file,
      }, {
        headers: authHeaders,
      });
      toast.success('Version wiederhergestellt. Der vorherige Stand wurde vorher gesichert.');
      setPreviewVersion(null);
      await loadState();
      await loadVersions();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Wiederherstellen fehlgeschlagen.');
    } finally {
      setVersionBusyFile(null);
    }
  }, [apiBase, authHeaders, isAuthenticated, loadState, loadVersions, stateKey]);

  const handleExportAllStates = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    setExporting(true);
    try {
      const response = await axios.get(`${apiBase}/contracts/state`, {
        params: { resource: 'export' },
        headers: authHeaders,
        responseType: 'blob',
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const fileName = (match && match[1])
        || `enpower-vertragsvorlagen-${new Date().toISOString().slice(0, 10)}.json`;
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json;charset=utf-8' });
      downloadBlob(blob, fileName);
      toast.success('Alle gespeicherten Vorlagen wurden als Datei exportiert.');
    } catch (error) {
      console.error(error);
      toast.error(await readAxiosErrorDetail(error, 'Export fehlgeschlagen.'));
    } finally {
      setExporting(false);
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  return (
    <div className="responsive-workspace responsive-workspace--templates" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Kopf mit Titel und Erklaerzeile ("Editier-Logik, Auswahl, Spacing
          und Pagination laufen im Tool ...") entfallen am 23.09.2026 - wie
          im Vertragsstudio am 22.09.: Die Navigation sagt schon, wo man ist. */}
      <div className="responsive-control-bar" style={{
        padding: '12px 20px',
        borderBottom: `1px solid ${T.randWeich}`,
        background: T.grund,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 280 }}>
          <label style={FELD_BESCHRIFTUNG}>{typeLabel}</label>
          <select
            value={activeContractKey}
            onChange={(event) => {
              const nextContract = documentOptions.find((contract) => contract.key === event.target.value);
              setActiveContractKey(event.target.value);
              if (nextContract && !nextContract.variants.find((v) => v.key === activeVariantKey)) {
                setActiveVariantKey(nextContract.variants[0].key);
              }
            }}
            style={{
              padding: '8px 10px',
              border: '1px solid #CBD5E1',
              borderRadius: 8,
              background: '#FFFFFF',
              color: '#0F172A',
              fontSize: 13,
              fontWeight: 600,
              minHeight: 36,
            }}
          >
            {documentOptions.map((contract) => (
              <option key={contract.key} value={contract.key}>{contract.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={FELD_BESCHRIFTUNG}>Variante</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {activeContract.variants.map((variant) => (
              <button
                key={variant.key}
                onClick={() => setActiveVariantKey(variant.key)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid',
                  borderColor: activeVariantKey === variant.key ? '#F28A0D' : '#CBD5E1',
                  borderRadius: 8,
                  background: activeVariantKey === variant.key ? '#F28A0D' : '#FFFFFF',
                  color: activeVariantKey === variant.key ? '#FFFFFF' : '#0F172A',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  minHeight: 36,
                }}
              >
                {variant.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <div className="responsive-action-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Kein Übertragen-Knopf mehr: CI und Druckversion teilen sich einen
              gespeicherten Stand (buildContractStateKey kennt keine Variante).
              Der Knopf sendete dieselbe Nutzlast an denselben Schlüssel wie
              "Speichern" und suggerierte einen Abgleich, den es nicht gibt. */}
          {enableSiblingSync && siblingVariant ? (
            <span
              title={`Gespeicherte Stände gelten für „${activeVariant.label}" und „${siblingVariant.label}" gemeinsam.`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: T.tMikro, fontWeight: 600, color: T.muted, marginRight: 4 }}
            >
              <GitMerge size={13} />
              {`gilt auch für „${siblingVariant.label}"`}
            </span>
          ) : null}
          <button
            onClick={handleToggleVersions}
            disabled={!isAuthenticated}
            title={isAuthenticated ? 'Frühere Stände dieser Vorlage ansehen und wiederherstellen' : 'Bitte erst einloggen'}
            style={{ ...knopfStil(versionsOpen), padding: '8px 12px', fontSize: T.tSm, minHeight: 36, opacity: !isAuthenticated ? 0.6 : 1 }}
          >
            <History size={14} />
            Versionen
          </button>
          {/* Seltenes steht seit dem 23.09.2026 unter "Mehr" - vorher sieben
              gleich gewichtete Knoepfe in dieser Leiste. */}
          <MehrMenue
            stil={{ padding: '8px 12px', fontSize: T.tSm, minHeight: 36 }}
            eintraege={[
              {
                key: 'original',
                label: `${originalButtonLabel} in neuem Tab`,
                icon: ExternalLink,
                title: 'Zum Vergleichen',
                onClick: handleOpenInNewTab,
              },
              enablePaymentSync && paymentSiblingContract ? {
                key: 'ezjz',
                label: paymentSyncing ? 'Überträgt …' : `Auf „${paymentSiblingContract.label}" übertragen`,
                icon: GitMerge,
                title: isAuthenticated ? 'Gemeinsame Bereiche übertragen. EZ/JZ-Schutz bleibt erhalten.' : 'Bitte erst einloggen',
                disabled: paymentSyncing || saving || !isAuthenticated,
                onClick: handleSyncToPaymentSibling,
              } : null,
              enableExampleClauses ? {
                key: 'beispiel',
                label: 'Markierten Absatz als Beispiel-Paragraph',
                icon: Archive,
                title: isAuthenticated ? undefined : 'Bitte erst einloggen',
                disabled: saving || !isAuthenticated,
                onClick: openExampleClauseDraft,
              } : null,
              {
                key: 'export',
                label: exporting ? 'Exportiert …' : 'Alle Vorlagen exportieren (JSON)',
                icon: Download,
                title: isAuthenticated ? undefined : 'Bitte erst einloggen',
                disabled: exporting || !isAuthenticated,
                onClick: handleExportAllStates,
              },
              {
                key: 'zuruecksetzen',
                label: 'Auf Original zurücksetzen',
                icon: RotateCcw,
                gefahr: true,
                disabled: saving || !hasSavedState,
                onClick: handleResetToOriginal,
              },
            ]}
          />
          <button
            onClick={handleSave}
            disabled={saving || !isAuthenticated}
            title={isAuthenticated
              ? (savedAt ? `Zuletzt gespeichert: ${new Date(savedAt).toLocaleString('de-DE')}` : 'Aktuellen Stand speichern')
              : 'Bitte erst einloggen'}
            style={{ ...primaerKnopfStil, padding: '8px 16px', fontSize: T.tSm, minHeight: 36, cursor: saving || !isAuthenticated ? 'not-allowed' : 'pointer', opacity: saving || !isAuthenticated ? 0.6 : 1 }}
          >
            <Save size={14} />
            {saving ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      </div>

      {versionsOpen ? (
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid #E2E8F0',
          background: '#FFFFFF',
          maxHeight: 320,
          overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <History size={16} color="#122A5C" />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 15, fontWeight: 700, color: '#122A5C' }}>
                Frühere Stände – {activeContract.label}
              </div>
              <div style={{ color: '#64748B', fontSize: 12, lineHeight: 1.5 }}>
                „Vorschau" lädt einen Stand nur in den Editor, „Wiederherstellen" macht ihn zum aktuellen – der bisherige wird vorher gesichert.
              </div>
            </div>
            <button
              type="button"
              onClick={loadVersions}
              disabled={versionsLoading}
              style={{
                border: '1px solid #CBD5E1',
                background: '#FFFFFF',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 700,
                color: '#475569',
                cursor: versionsLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {versionsLoading ? 'Lade…' : 'Aktualisieren'}
            </button>
            <button
              type="button"
              onClick={() => setVersionsOpen(false)}
              style={{
                border: '1px solid #CBD5E1',
                background: '#FFFFFF',
                borderRadius: 8,
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 700,
                color: '#475569',
              }}
            >
              <XIcon size={13} />
              Schließen
            </button>
          </div>

          {versionsLoading ? (
            <div style={{ color: '#64748B', fontSize: 13, padding: '8px 0' }}>Versionen werden geladen…</div>
          ) : versions.length === 0 ? (
            <div style={{ color: '#64748B', fontSize: 13, padding: '8px 0' }}>
              Für diese Vorlage sind noch keine früheren Stände vorhanden.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {versions.map((version) => {
                const busy = versionBusyFile === version.file;
                const isPreviewing = previewVersion?.file === version.file;
                return (
                  <div
                    key={version.file}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 10,
                      border: '1px solid',
                      borderColor: isPreviewing ? '#F28A0D' : '#E2E8F0',
                      background: isPreviewing ? '#FFF7ED' : '#F8FAFC',
                      borderRadius: 10,
                      padding: '8px 12px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
                        {formatVersionDate(version.createdAt)}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748B' }}>
                        {version.actor || 'unbekannt'}
                        {formatVersionSize(version.sizeBytes) ? ` · ${formatVersionSize(version.sizeBytes)}` : ''}
                        {isPreviewing ? ' · aktuell in der Vorschau' : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePreviewVersion(version)}
                      disabled={busy}
                      style={{
                        border: '1px solid #CBD5E1',
                        background: '#FFFFFF',
                        borderRadius: 8,
                        padding: '7px 11px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#475569',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        opacity: busy ? 0.6 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <Eye size={13} />
                      Vorschau
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRestoreVersion(version)}
                      disabled={busy}
                      style={{
                        border: '1px solid #122A5C',
                        background: '#122A5C',
                        borderRadius: 8,
                        padding: '7px 11px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#FFFFFF',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        opacity: busy ? 0.6 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <RotateCcw size={13} />
                      {busy ? 'Bitte warten…' : 'Wiederherstellen'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {previewVersion ? (
        <div style={{
          padding: '10px 20px',
          background: '#FFF7ED',
          borderBottom: '1px solid #FDBA74',
          color: '#9A3412',
          fontSize: 12,
          lineHeight: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
        }}>
          <Eye size={14} />
          <strong>Vorschau einer früheren Version</strong>
          <span>
            ({formatVersionDate(previewVersion.createdAt)}
            {previewVersion.actor ? ` · ${previewVersion.actor}` : ''}) – dieser Stand ist NICHT gespeichert.
            Zum Übernehmen „Speichern" oder „Wiederherstellen" nutzen.
          </span>
          <button
            type="button"
            onClick={handleDiscardPreview}
            style={{
              border: '1px solid #FDBA74',
              background: '#FFFFFF',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 700,
              color: '#9A3412',
              cursor: 'pointer',
            }}
          >
            Vorschau verwerfen
          </button>
        </div>
      ) : null}

      {enableExampleClauses && exampleClauseDraft ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(15, 23, 42, 0.44)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: 'min(920px, 100%)', maxHeight: 'min(860px, 100%)', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 24px 80px rgba(15, 23, 42, 0.35)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 700, color: '#122A5C' }}>Beispiel-Paragraph speichern</div>
                <div style={{ color: '#64748B', fontSize: 13 }}>Wähle die genaue Einfügestelle im Vertragskontext und sieh die Vorschau vor dem Speichern.</div>
              </div>
              <button
                type="button"
                onClick={handleCloseExampleClauseDraft}
                style={{ border: '1px solid #CBD5E1', background: '#FFFFFF', borderRadius: 10, padding: '8px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <XIcon size={14} />
                Schließen
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', minHeight: 0, flex: 1 }}>
              <div style={{ padding: 18, borderRight: '1px solid #E2E8F0', overflowY: 'auto', background: '#F8FAFC' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Titel</label>
                <input
                  value={exampleClauseDraft.title}
                  onChange={(event) => setExampleClauseDraft((current) => ({ ...current, title: event.target.value }))}
                  style={{ width: '100%', padding: '9px 11px', border: '1px solid #CBD5E1', borderRadius: 10, fontSize: 13, boxSizing: 'border-box', marginBottom: 14 }}
                />

                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Einfügeposition</label>
                <select
                  value={exampleClauseDraft.anchorSide}
                  onChange={(event) => setExampleClauseDraft((current) => ({ ...current, anchorSide: event.target.value }))}
                  style={{ width: '100%', padding: '9px 11px', border: '1px solid #CBD5E1', borderRadius: 10, fontSize: 13, boxSizing: 'border-box', marginBottom: 14 }}
                >
                  <option value="before">Vor {exampleClauseDraft.structureLabel}</option>
                  <option value="after">Nach {exampleClauseDraft.structureLabel}</option>
                </select>

                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginBottom: 14 }}>
                  Zielblock: <strong>{exampleClauseDraft.anchorLabel || exampleClauseDraft.anchorBlockId || 'unbekannt'}</strong>
                </div>
                {exampleClauseDraft.sectionLabel ? (
                  <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5, marginBottom: 14 }}>
                    Erkannt im Abschnitt: <strong>{exampleClauseDraft.sectionLabel}</strong>
                  </div>
                ) : null}
                <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5, marginBottom: 14 }}>
                  Erkannte Struktur: <strong>{exampleClauseDraft.structureLabel}</strong>
                </div>

                <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, background: '#FFFFFF', padding: 12, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Vorschau</div>
                  <div style={{ fontSize: 12, color: '#334155', marginBottom: 10 }}>
                    {exampleClauseDraft.anchorSide === 'before' ? 'Der Beispiel-Paragraph erscheint vor dem gewählten Absatz.' : 'Der Beispiel-Paragraph erscheint nach dem gewählten Absatz.'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {exampleClauseDraft.anchorSide === 'before' ? (
                      <>
                        <div style={{ border: '1px solid #F59E0B', background: '#FFF7ED', borderRadius: 10, padding: '10px 12px', fontSize: 12, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: exampleClauseDraft.contentHtml }} />
                        <div style={{ border: '1px dashed #CBD5E1', borderRadius: 10, padding: '10px 12px', fontSize: 12, lineHeight: 1.5, color: '#0F172A', background: '#FFFFFF' }}>{exampleClauseDraft.anchorLabel}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ border: '1px dashed #CBD5E1', borderRadius: 10, padding: '10px 12px', fontSize: 12, lineHeight: 1.5, color: '#0F172A', background: '#FFFFFF' }}>{exampleClauseDraft.anchorLabel}</div>
                        <div style={{ border: '1px solid #F59E0B', background: '#FFF7ED', borderRadius: 10, padding: '10px 12px', fontSize: 12, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: exampleClauseDraft.contentHtml }} />
                      </>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handlePersistExampleClause}
                    disabled={saving || !exampleClauseDraft.title.trim()}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #122A5C', borderRadius: 10, background: saving ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 13, fontWeight: 700, cursor: saving || !exampleClauseDraft.title.trim() ? 'not-allowed' : 'pointer' }}
                  >
                    {saving ? 'Speichere…' : 'Speichern'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseExampleClauseDraft}
                    style={{ padding: '10px 14px', border: '1px solid #CBD5E1', borderRadius: 10, background: '#FFFFFF', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>

              <div style={{ minWidth: 0, overflow: 'auto', padding: 18, background: '#FFFFFF' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Vertragsansicht</div>
                <div style={{ border: '1px solid #E2E8F0', borderRadius: 14, overflow: 'hidden', background: '#F8FAFC', padding: 14 }}>
                  <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.55, marginBottom: 10 }}>
                    Die Hauptansicht bleibt im Hintergrund offen. Der markierte Absatz im Vertrag ist der Ankerpunkt für den Beispiel-Paragraphen.
                  </div>
                  <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>Ausgewählter Anker:</div>
                  <div style={{ border: '1px dashed #CBD5E1', borderRadius: 10, padding: 12, background: '#FFFFFF', fontSize: 12, lineHeight: 1.5 }}>
                    {exampleClauseDraft.anchorLabel}
                  </div>
                </div>
                <div style={{ marginTop: 10, color: '#64748B', fontSize: 12, lineHeight: 1.5 }}>
                  Wenn du eine andere Stelle brauchst, schließe den Dialog, wähle im Vertrag einen anderen Absatz und öffne die Vorschau erneut.
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Der Normalfall "Gespeicherter Stand geladen · zuletzt am ..." stand bis
          zum 23.09.2026 als eigene Zeile hier; das Datum steht jetzt am
          Speichern-Knopf. Eine Zeile gibt es nur noch, wenn etwas anders ist. */}
      {!loadingState && (!hasSavedState || syncedFromVariant) ? (
        <div style={{ padding: '6px 20px', background: T.warnGrund, color: T.warn, fontSize: T.tMeta, borderBottom: `1px solid ${T.warnRand}` }}>
          {hasSavedState
            ? `Aus „${(activeContract.variants.find((v) => v.key === syncedFromVariant) || {}).label || syncedFromVariant}" übernommen – speichern, um diese Variante eigenständig festzuhalten.`
            : `Für diesen ${recordLabel} ist noch kein Stand gespeichert – es gilt die Originaldatei.`}
        </div>
      ) : null}

      <div className="responsive-preview-pane" style={{ flex: 1, minHeight: 0, background: '#E8EEF6', position: 'relative' }}>
        {iframeSrcDoc ? (
          <iframe
            key={iframeKey}
            ref={iframeRef}
            title={`${activeContract.label} — ${activeVariant.label}`}
            srcDoc={iframeSrcDoc}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        ) : iframeSrc ? (
          <iframe
            key={iframeKey}
            ref={iframeRef}
            title={`${activeContract.label} — ${activeVariant.label}`}
            src={iframeSrc}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        ) : null}
      </div>
    </div>
  );
}
